import { Stack, Typography } from '@mui/material';
import { ComponentType, MouseEvent } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { IconProps } from '@/components/icons/Image01Icon';
import { Maximize01Icon } from '@/components/icons/Maximize01Icon';
import { Card } from '@/components/layout/Card/Card';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';

const ARTIFACT_ICON_SIZE_PX = 16;

/**
 * Content sits 8px inside the card's outer edge, which includes the 1px
 * border. A single-line card is then 32px tall.
 */
const CARD_PADDING_PX = 7;

/**
 * Each action button is 24px: a 16px glyph, 3px padding, and the button's
 * 1px transparent border. The action group cancels the 4px glyph inset with
 * a negative margin, so the buttons do not make the card taller, and
 * adjacent glyphs are 8px apart.
 */
const ACTION_BUTTON_SX = { padding: '3px' };
const ACTION_GLYPH_INSET_PX = 4;

interface ArtifactMessageCardProps {
  /** Glyph that identifies the artifact type, such as a diagram. */
  icon: ComponentType<IconProps>;
  title: string;
  /**
   * Opens the artifact. A click on the card or on the open action calls it.
   * Omit it when the viewer cannot open the artifact; the card then shows
   * only the icon and title.
   */
  onOpen?: () => void;
  /** Downloads the artifact. Omit it to hide the download action. */
  onDownload?: () => void;
}

/**
 * Shared shell for artifact messages: assistant messages that deliver a
 * saved artifact, such as a diagram, into the chat transcript. Each artifact
 * message type supplies its icon, title, and actions.
 */
export function ArtifactMessageCard({
  icon: Icon,
  title,
  onOpen,
  onDownload,
}: ArtifactMessageCardProps) {
  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: `${CARD_PADDING_PX}px`,
        borderRadius: '4px',
        borderColor: COLORS.GRAY[300],
        cursor: onOpen ? 'pointer' : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Stack direction="row" gap={0.75} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Icon size={ARTIFACT_ICON_SIZE_PX} color={COLORS.TEXT.SECONDARY} />
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ m: `-${ACTION_GLYPH_INSET_PX}px` }}>
          {onDownload && (
            <ArtifactAction
              label="Download"
              icon={Download01Icon}
              onClick={onDownload}
            />
          )}
          {onOpen && (
            <ArtifactAction
              label="Open"
              icon={Maximize01Icon}
              onClick={onOpen}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

function ArtifactAction({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ComponentType<IconProps>;
  onClick: () => void;
}) {
  return (
    <Tooltip placement="bottom" title={label}>
      <IconButton
        ariaLabel={label}
        icon={icon}
        size="xs"
        variant="transparent"
        sx={ACTION_BUTTON_SX}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          // The card also handles clicks. Without this, the open action
          // would open the artifact twice, and download would also open it.
          event.stopPropagation();
          onClick();
        }}
      />
    </Tooltip>
  );
}
